/**
 * GrapesJS Open Source Email Editor
 *
 * Open-source implementation with full control over:
 * - Asset management (MediaPicker integration)
 * - Custom theming (Pumpd brand)
 * - Variable insertion
 * - Storage persistence
 */
import React, { useEffect, useRef, useState } from 'react';
import grapesjs, { Editor } from 'grapesjs';
import gjsMJML from 'grapesjs-mjml';
import mjml2html from 'mjml-browser';
import 'grapesjs/dist/css/grapes.min.css';
import './grapes-pumpd-theme.css';
import { sanitizeGrapesJSProjectData } from '@/utils/sanitize-grapes-data';

import { useAuth } from '@/context/auth';
import MediaService, { RestaurantMedia } from '@/api/media';
import EmailService from '@/api/email';
// REMOVED: MediaPicker import - now using native GrapesJS AssetManager
// import { MediaPicker } from '@/components/media/MediaPicker';
import { toast } from '@/hooks/use-toast';
import { VariableInsertionModal } from './variables';
import type { VariableInsertionMode } from './variables';
import { isUrlVariable } from './variables/types';
import type { PromoCode } from '@/hooks/sms/types';
import { useRestaurantContext } from '@/context/RestaurantContext';

interface GrapesJSEditorProps {
  /**
   * Initial HTML content to load
   */
  initialHtml?: string;

  /**
   * Initial GrapesJS project data (JSON)
   */
  initialProject?: any;

  /**
   * Campaign or template ID for saving
   */
  projectId?: string;

  /**
   * Type of project (campaign, template, or email_trigger)
   */
  projectType?: 'campaign' | 'template' | 'email_trigger';

  /**
   * Callback when content changes
   */
  onChange?: (html: string, css: string, project: any) => void;

  /**
   * Callback to expose manual save function and editor instance
   */
  onEditorReady?: (saveFunction: () => Promise<void>, editorInstance?: any) => void;

  /**
   * Height of the editor
   */
  height?: string;
}

const GrapesJSEditor: React.FC<GrapesJSEditorProps> = ({
  initialHtml = '',
  initialProject,
  projectId,
  projectType = 'campaign',
  onChange,
  onEditorReady,
  height = '800px'
}) => {
  const { user } = useAuth();
  const { restaurantDetails } = useRestaurantContext();
  const editorRef = useRef<Editor | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Variable insertion modal state
  const [showVariableModal, setShowVariableModal] = useState(false);
  const [variableModalMode, setVariableModalMode] = useState<VariableInsertionMode>('personalization');
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');

  // RTE cursor-aware insertion state (Phase 3)
  const [insertionMode, setInsertionMode] = useState<'append' | 'cursor'>('append');
  const rteRef = useRef<any>(null);
  // Store cursor position as character offset (Range objects become invalid on blur)
  const savedSelectionRef = useRef<{ offset: number; contentDiv: HTMLElement | null }>({ offset: 0, contentDiv: null });

  // Format restaurants for modal
  const restaurants = React.useMemo(() => {
    return (restaurantDetails || []).map(r => ({
      id: String(r.id),
      name: r.name,
      ordering_page_url: r.ordering_page_url,
      review_page_url: r.review_page_url,
      google_review_url: r.google_review_url,
      facebook_url: r.facebook_url,
      instagram_url: r.instagram_url
    }));
  }, [restaurantDetails]);

  useEffect(() => {
    if (!containerRef.current || !user?.organisationId) return;

    const initEditor = async () => {
      try {
        setIsLoading(true);

        console.log('[GrapesJS] Initializing open-source editor...');

        // Initialize GrapesJS Editor
        const editor = grapesjs.init({
          container: containerRef.current!,
          height,
          fromElement: false,

          // Storage Manager
          storageManager: {
            type: 'remote', // Remote storage with custom onLoad/onStore handlers
            autosave: false, // Disable autosave to prevent style attribute duplication bug
            autoload: false, // Disable autoload, we'll load manually
            stepsBeforeSave: 1, // Only save when explicitly called

            options: {
              remote: {
              onLoad: async () => {
                try {
                  if (!projectId) {
                    console.log('[Storage] No project ID, returning empty project');
                    const initial = initialProject || {};
                    return sanitizeGrapesJSProjectData(initial);
                  }

                  console.log('[Storage] Loading project:', projectId);

                  let loadedProject;
                  if (projectType === 'campaign') {
                    const campaign = await EmailService.getCampaign(projectId);
                    loadedProject = campaign.design_json;
                  } else if (projectType === 'template') {
                    const template = await EmailService.getTemplate(projectId);
                    loadedProject = template.design_json;
                  } else if (projectType === 'email_trigger') {
                    // ✅ Dynamic import to avoid circular dependency
                    const EmailTriggerService = (await import('@/api/emailTriggers')).default;
                    const trigger = await EmailTriggerService.getTrigger(projectId, user?.organisationId || '');
                    loadedProject = trigger.email_design_json;
                  }

                  // Sanitize loaded project to fix any style attribute corruption from autosave bug
                  const projectToLoad = loadedProject || initialProject || {};
                  const sanitizedProject = sanitizeGrapesJSProjectData(projectToLoad);

                  console.log('[Storage] Project loaded and sanitized successfully');
                  return sanitizedProject;

                } catch (error) {
                  console.error('[Storage] Load error:', error);
                  toast({
                    title: 'Load Error',
                    description: 'Failed to load project data',
                    variant: 'destructive'
                  });
                  const fallback = initialProject || {};
                  return sanitizeGrapesJSProjectData(fallback);
                }
              },

              onStore: async (projectData: any) => {
                try {
                  setSaveStatus('saving');

                    if (!projectId) {
                      console.warn('[Storage] No project ID provided, skipping save');
                      setSaveStatus('idle');
                      return;
                    }

                    console.log('[Storage] Saving project:', projectId);

                    // Get MJML markup from editor
                    const mjmlMarkup = editor.getHtml();
                    const css = editor.getCss();
                    console.log('[Storage] MJML markup length:', mjmlMarkup.length);

                    // Compile MJML to HTML using mjml-browser
                    let completeHtml = '';
                    try {
                      const mjmlResult = mjml2html(mjmlMarkup);

                      if (mjmlResult.errors && mjmlResult.errors.length > 0) {
                        console.warn('[Storage] MJML compilation warnings:', mjmlResult.errors);
                      }

                      completeHtml = mjmlResult.html;
                      console.log('[Storage] Compiled HTML length:', completeHtml.length);
                    } catch (error) {
                      console.error('[Storage] MJML compilation failed:', error);
                      // Fallback to raw MJML if compilation fails
                      completeHtml = mjmlMarkup;
                    }

                    // Generate plain text version - strip HTML tags, style blocks, and scripts
                    let plainText = completeHtml
                      // Remove head section (contains styles, meta, etc.)
                      .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
                      // Remove style blocks
                      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                      // Remove script blocks
                      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                      // Remove XML/Office tags (<!--[if mso]> ... <![endif]-->)
                      .replace(/<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, '')
                      // Remove HTML comments
                      .replace(/<!--[\s\S]*?-->/g, '')
                      // Remove all HTML tags
                      .replace(/<[^>]+>/g, '')
                      // Remove HTML entities
                      .replace(/&nbsp;/g, ' ')
                      .replace(/&amp;/g, '&')
                      .replace(/&lt;/g, '<')
                      .replace(/&gt;/g, '>')
                      .replace(/&quot;/g, '"')
                      // Collapse multiple spaces/newlines to single space
                      .replace(/\s+/g, ' ')
                      // Trim whitespace
                      .trim();

                    console.log('[Storage] Plain text length:', plainText.length);

                    // Save based on project type
                    if (projectType === 'campaign') {
                      await EmailService.updateCampaign(projectId, {
                        design_json: projectData,
                        campaign_content_html: completeHtml,
                        campaign_content_text: plainText,
                      });
                    } else if (projectType === 'template') {
                      await EmailService.updateTemplate(projectId, {
                        design_json: projectData,
                        template_content_html: completeHtml,
                        template_content_text: plainText,
                      });
                    } else if (projectType === 'email_trigger') {
                      // ✅ Dynamic import to avoid circular dependency
                      const EmailTriggerService = (await import('@/api/emailTriggers')).default;
                      await EmailTriggerService.updateTrigger(projectId, {
                        email_design_json: projectData,
                        email_content_html: completeHtml,
                        email_content_text: plainText,
                      });
                    }

                    setSaveStatus('saved');
                    console.log('[Storage] Project saved successfully');

                    toast({
                      title: 'Saved',
                      description: `${projectType === 'campaign' ? 'Campaign' : 'Template'} saved successfully`,
                    });

                    // Trigger onChange callback
                    if (onChange) {
                      onChange(completeHtml, css, projectData);
                    }

                    // Reset to idle after 2 seconds
                    setTimeout(() => setSaveStatus('idle'), 2000);

                  } catch (error) {
                    console.error('[Storage] Save error:', error);
                    setSaveStatus('error');

                    toast({
                      title: 'Save Error',
                      description: 'Failed to save changes',
                      variant: 'destructive'
                    });

                    setTimeout(() => setSaveStatus('idle'), 3000);
                  }
                }
              }
            }
          },

          // Asset Manager - Use native GrapesJS modal with custom upload
          assetManager: {
            upload: false, // We handle uploads via uploadFile function
            assets: [], // Will be populated from Supabase on load
            autoAdd: 1, // Automatically add uploaded assets to collection

            // Custom file upload handler - integrates with Supabase
            uploadFile: async (e: any) => {
              const editor = editorRef.current;
              if (!editor) {
                console.error('[Assets] Editor not initialized');
                return;
              }

              try {
                const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;

                if (!files || files.length === 0) {
                  console.warn('[Assets] No files to upload');
                  return;
                }

                console.log(`[Assets] Uploading ${files.length} file(s)...`);

                // Upload each file using MediaService
                const uploadPromises = Array.from(files).map(async (file: any) => {
                  try {
                    const uploadedMedia = await MediaService.uploadImage({
                      file,
                      organisation_id: user!.organisationId,
                      tags: ['email-campaign'],
                      associations: {
                        source: 'email-editor',
                        campaign_id: projectId
                      }
                    });

                    // Return in GrapesJS expected format
                    return {
                      src: uploadedMedia.public_url,
                      name: uploadedMedia.original_filename || 'Uploaded Image',
                      type: 'image',
                      id: uploadedMedia.id,
                      height: uploadedMedia.height,
                      width: uploadedMedia.width
                    };
                  } catch (error) {
                    console.error('[Assets] Upload failed for file:', file.name, error);
                    toast({
                      title: 'Upload Failed',
                      description: `Failed to upload ${file.name}`,
                      variant: 'destructive'
                    });
                    return null;
                  }
                });

                const uploadedAssets = await Promise.all(uploadPromises);
                const successfulAssets = uploadedAssets.filter(asset => asset !== null);

                if (successfulAssets.length > 0) {
                  console.log(`[Assets] Successfully uploaded ${successfulAssets.length} asset(s)`);

                  // Manually add assets to AssetManager for immediate visibility
                  editor.AssetManager.add(successfulAssets);
                  console.log('[Assets] Assets added to AssetManager');

                  toast({
                    title: 'Upload Successful',
                    description: `${successfulAssets.length} image(s) uploaded successfully`,
                  });

                  // Return response for GrapesJS (for consistency with standard behavior)
                  return { data: successfulAssets };
                }
              } catch (error) {
                console.error('[Assets] Upload error:', error);
                toast({
                  title: 'Upload Error',
                  description: 'Failed to upload images',
                  variant: 'destructive'
                });
              }
            },
          },

          // Device Manager - Using MJML's built-in devices (resetDevices: true)
          // MJML will provide: Desktop (default), Mobile (320px), and Tablet (768px)

          // Rich Text Editor - Disable problematic actions for email
          richTextEditor: {
            actions: ['bold', 'italic', 'underline', 'strikethrough', 'link']
            // Removed: 'wrap' (creates problematic spans with inline styles)
            // Text alignment should be controlled via Style Manager MJML attributes
          },

          // Plugins
          plugins: [gjsMJML],

          // MJML Plugin options
          pluginsOpts: {
            [gjsMJML]: {
              // Reset blocks to use MJML's organized blocks
              resetBlocks: true,
              resetDevices: true, // Use MJML's responsive device presets (Desktop, Mobile, Tablet)
              resetStyleManager: true, // Use MJML's custom Style Manager with proper MJML attributes

              // Category for MJML blocks
              categoryLabel: 'Email Components',

              // Fonts - MJML plugin will add these to <mj-head> and Style Manager
              fonts: {
                // Custom Google Fonts (must be URLs for MJML to add to <mj-head>)
                'Gabarito': 'https://fonts.googleapis.com/css?family=Gabarito:400,500,600,700,800,900',
                'Inter': 'https://fonts.googleapis.com/css?family=Inter:400,500,600,700',
                'Montserrat': 'https://fonts.googleapis.com/css?family=Montserrat:400,500,600,700',
                'Open Sans': 'https://fonts.googleapis.com/css?family=Open+Sans:400,600,700',
                'Poppins': 'https://fonts.googleapis.com/css?family=Poppins:400,500,600,700',
                'Roboto': 'https://fonts.googleapis.com/css?family=Roboto:400,500,700',
              },

              // Column padding
              columnsPadding: '10px 5px',

              // Use a data URI placeholder instead of via.placeholder.com to avoid network errors
              imagePlaceholderSrc: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzUwIiBoZWlnaHQ9IjI1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzUwIiBoZWlnaHQ9IjI1MCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5DbGljayB0byBzZWxlY3QgaW1hZ2U8L3RleHQ+PC9zdmc+',

              // Default MJML template with Pumpd branding and Gabarito as default font
              preMjml: `<mjml>
  <mj-head>
    <mj-font name="Gabarito" href="https://fonts.googleapis.com/css?family=Gabarito:400,500,600,700,800,900" />
    <mj-font name="Inter" href="https://fonts.googleapis.com/css?family=Inter" />
    <mj-font name="Montserrat" href="https://fonts.googleapis.com/css?family=Montserrat:400,500,600,700" />
    <mj-font name="Poppins" href="https://fonts.googleapis.com/css?family=Poppins:400,500,600,700" />
    <mj-attributes>
      <!-- Set Gabarito as default font for ALL components -->
      <mj-all font-family="Gabarito, Arial, sans-serif" />
      <mj-text font-size="14px" color="#383838" line-height="1.6" font-family="Gabarito, Arial, sans-serif" />
      <mj-button background-color="#3F92FF" color="#FFFFFF" border-radius="4px" font-family="Gabarito, Arial, sans-serif" font-weight="600" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#F5F5F5">
    <mj-section background-color="#FFFFFF" padding="20px">
      <mj-column>
        <mj-text font-family="Gabarito, Arial, sans-serif">
          <h2>Start building your email</h2>
          <p>Drag blocks from the right panel to add content.</p>
        </mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`,
            }
          }
        });

        // Store editor reference
        editorRef.current = editor;

        // Configure Style Manager fonts after MJML plugin initializes
        // MJML's resetStyleManager creates its own structure that we need to modify
        const configureFonts = () => {
          const styleManager = editor.StyleManager;
          const sectors = styleManager.getSectors();

          // Debug: Log all sectors and their properties
          console.log('[Editor] Available Style Manager sectors:', sectors.models?.map((s: any) => ({
            id: s.get('id'),
            name: s.get('name'),
            properties: s.get('properties')?.models?.map((p: any) => ({
              name: p.get('name'),
              property: p.get('property'),
              type: p.get('type')
            }))
          })));

          let fontProperty = null;

          // Search through all sectors for the font-family property
          sectors.forEach((sector: any) => {
            const properties = sector.get ? sector.get('properties') : sector.properties;
            if (properties) {
              const props = properties.models || properties;
              props.forEach((p: any) => {
                const propName = p.get('property') || p.get('name');
                if (propName === 'font-family') {
                  fontProperty = p;
                  console.log('[Editor] Found font-family property in sector:', sector.get ? sector.get('name') : sector.name);
                }
              });
            }
          });

          if (fontProperty) {
            // Add web-safe fonts + our custom Google Fonts to the dropdown
            fontProperty.set('options', [
              // Standard web-safe fonts (always available in email clients)
              { value: 'Arial, sans-serif', name: 'Arial' },
              { value: 'Georgia, serif', name: 'Georgia' },
              { value: 'Helvetica, Arial, sans-serif', name: 'Helvetica' },
              { value: 'Tahoma, sans-serif', name: 'Tahoma' },
              { value: '"Times New Roman", Times, serif', name: 'Times New Roman' },
              { value: 'Verdana, sans-serif', name: 'Verdana' },
              // Custom Google Fonts (loaded via MJML <mj-font> tags)
              { value: 'Gabarito, Arial, sans-serif', name: 'Gabarito ⭐' },
              { value: 'Inter, Arial, sans-serif', name: 'Inter' },
              { value: 'Montserrat, Arial, sans-serif', name: 'Montserrat' },
              { value: '"Open Sans", Arial, sans-serif', name: 'Open Sans' },
              { value: 'Poppins, Arial, sans-serif', name: 'Poppins' },
              { value: 'Roboto, Arial, sans-serif', name: 'Roboto' },
            ]);
            console.log('[Editor] ✓ Custom fonts added to MJML Style Manager');
          } else {
            console.error('[Editor] ✗ Font-family property not found in MJML Style Manager');
          }
        };

        // Try to configure fonts after MJML loads
        // Use a small delay to ensure MJML's Style Manager is fully initialized
        setTimeout(() => {
          configureFonts();
        }, 100);

        // Set Gabarito as default for MJML components
        const domComponents = editor.DomComponents;

        // Get MJML text component type and set default font
        const mjTextType = domComponents.getType('mj-text');
        if (mjTextType) {
          const defaultModel = mjTextType.model;
          const originalInit = defaultModel.prototype.init;

          defaultModel.prototype.init = function() {
            if (originalInit) originalInit.apply(this, arguments);

            // Set default font-family attribute
            if (!this.get('font-family')) {
              this.addAttributes({ 'font-family': 'Gabarito, Arial, sans-serif' });
            }
          };
          console.log('[Editor] Gabarito set as default for mj-text components');
        }

        // Set Gabarito as default for MJML button component
        const mjButtonType = domComponents.getType('mj-button');
        if (mjButtonType) {
          const defaultModel = mjButtonType.model;
          const originalInit = defaultModel.prototype.init;

          defaultModel.prototype.init = function() {
            if (originalInit) originalInit.apply(this, arguments);

            // Set default font-family trait
            if (!this.get('font-family')) {
              this.addAttributes({ 'font-family': 'Gabarito, Arial, sans-serif' });
            }
          };
          console.log('[Editor] Gabarito set as default for mj-button components');
        }

        // Setup editor events
        setupEditorEvents(editor);

        // Wait for editor to be fully loaded before adding assets
        editor.on('load', async () => {
          console.log('[Editor] Load event fired, loading assets and project...');

          // Load initial assets from media library
          await loadInitialAssets(editor);

          // Load project data directly using our custom logic (bypass GrapesJS storage)
          if (projectId) {
            try {
              console.log('[Editor] Loading project data from database...');

              let loadedProject;
              if (projectType === 'campaign') {
                const campaign = await EmailService.getCampaign(projectId);
                loadedProject = campaign.design_json;
              } else if (projectType === 'template') {
                const template = await EmailService.getTemplate(projectId);
                loadedProject = template.design_json;
              } else if (projectType === 'email_trigger') {
                // ✅ Load email trigger design data
                const EmailTriggerService = (await import('@/api/emailTriggers')).default;
                const trigger = await EmailTriggerService.getTrigger(projectId, user?.organisationId || '');
                loadedProject = trigger.email_design_json;
              }

              if (loadedProject) {
                editor.loadProjectData(loadedProject);
                console.log('[Editor] Project data loaded successfully');
              } else {
                console.log('[Editor] No project data found, loading starter template');
                // No saved data, load starter template
                loadStarterTemplate(editor);
              }
            } catch (error) {
              console.error('[Editor] Error loading project:', error);
              // On error, load starter template
              loadStarterTemplate(editor);
            }
          } else if (initialProject) {
            console.log('[Editor] Loading initialProject data...');
            editor.loadProjectData(initialProject);
            console.log('[Editor] initialProject data loaded');
          } else if (initialHtml) {
            editor.setComponents(initialHtml);
          } else {
            // No initial data at all, load starter template
            loadStarterTemplate(editor);
          }

          // IMPORTANT: Reload user assets AFTER loadProjectData
          // loadProjectData() replaces the entire editor state including AssetManager
          // This ensures user's media library is always shown, not template creator's embedded assets
          console.log('[Editor] Reloading user assets after project load...');
          await loadInitialAssets(editor);
          console.log('[Editor] User assets reloaded successfully');

          // ========== FIX: Setup global cursor tracking AFTER canvas is ready ==========
          // ISSUE: GrapesJS uses iframe - need iframe's window.getSelection(), not parent window
          // SOLUTION: Get iframe's window and use that for selection
          const canvasWindow = editor.Canvas.getWindow(); // Iframe's window
          const canvas = editor.Canvas.getBody(); // Iframe's body

          console.log('[DEBUG] Canvas after load:', {
            canvas: canvas?.nodeName,
            canvasWindow: canvasWindow?.constructor.name
          });

          const saveCurrentCursorPosition = () => {
            // Use IFRAME's selection, not parent window's selection!
            const selection = canvasWindow?.getSelection?.();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              const container = range.startContainer;

              // Only save if cursor is in a contenteditable element (RTE is active)
              let contentDiv = container.nodeType === Node.ELEMENT_NODE
                ? container as HTMLElement
                : container.parentElement;

              // Find the contenteditable ancestor
              while (contentDiv && !contentDiv.hasAttribute('contenteditable')) {
                contentDiv = contentDiv.parentElement;
              }

              if (contentDiv && contentDiv.getAttribute('contenteditable') === 'true') {
                // ========== FIX: Save as character offset, not Range ==========
                // ISSUE: Range objects become invalid when element loses focus
                // SOLUTION: Calculate character offset from start of contentDiv
                const textContent = contentDiv.textContent || '';
                const preRange = document.createRange();
                preRange.selectNodeContents(contentDiv);
                preRange.setEnd(range.startContainer, range.startOffset);
                const offset = preRange.toString().length;

                savedSelectionRef.current = { offset, contentDiv };
                console.log('[Editor] Cursor tracked at offset:', offset, 'of', textContent.length);
                // ========== END FIX ==========
              }
            }
          };

          if (canvas) {
            canvas.addEventListener('mouseup', saveCurrentCursorPosition);
            canvas.addEventListener('keyup', saveCurrentCursorPosition);
            canvas.addEventListener('click', saveCurrentCursorPosition);
            console.log('[Editor] ✓ Global cursor tracking enabled on canvas');
          } else {
            console.error('[Editor] ✗ Canvas still undefined after load event!');
          }
          // ========== END FIX ==========
        });

        // Images use native GrapesJS AssetManager
        // Users click on placeholder → native modal opens → select from Supabase images

        // REMOVED: Custom MediaPicker integration
        // We now use the native GrapesJS AssetManager flow
        // When users click on an mj-image placeholder, the native AssetManager opens automatically
        // and displays images we've pre-loaded from Supabase

        console.log('[Editor] Using native AssetManager for image selection');
        console.log('[Editor] AssetManager has', editor.AssetManager.getAll().length, 'assets loaded');

        // Debug: Log editor state for troubleshooting
        console.log('[GrapesJS] Available blocks:', editor.BlockManager.getAll().length);
        console.log('[GrapesJS] Block categories:', editor.BlockManager.getCategories().map((c: any) => c.id));
        console.log('[GrapesJS] Panels:', editor.Panels.getPanels().map((p: any) => p.id));

        // Ensure blocks panel is visible
        const blocksPanel = editor.Panels.getPanel('views');
        if (blocksPanel && !blocksPanel.get('visible')) {
          blocksPanel.set('visible', true);
        }

        // ====== VARIABLE INSERTION COMMANDS ======

        // Command: Insert variable into selected text or button component
        editor.Commands.add('insert-variable', {
          run(editor, sender, options: any = {}) {
            const variable = options.variable;
            const selected = editor.getSelected();

            if (!selected) {
              toast({
                title: 'No component selected',
                description: 'Please select a text block or button first',
                variant: 'destructive'
              });
              return;
            }

            const componentType = selected.get('type');

            // Handle text components
            if (componentType === 'mj-text') {
              // Get the view element
              const el = selected.getEl();

              if (el) {
                // MJML wraps content in table structure. Find the actual content div.
                // Look for the innermost div with contenteditable or the last div child
                const contentDiv = el.querySelector('div[contenteditable]') || el.querySelector('div:last-of-type') || el;

                // Get current text content (strips HTML tags)
                const textContent = (contentDiv.textContent || '').trim();
                // Get current HTML content from the inner div only
                let currentHtml = contentDiv.innerHTML || '';

                console.log('[Variable] Current text content:', textContent);
                console.log('[Variable] Current HTML content:', currentHtml);

                // Determine if component is effectively empty
                const isEmpty = textContent.length === 0;

                console.log('[Variable] Component is empty:', isEmpty);

                let newHtml;
                if (isEmpty) {
                  // Component is empty - just set the variable (no space, no HTML)
                  newHtml = variable;
                  console.log('[Variable] Empty component - replacing with variable only');
                } else {
                  // Component has text - append with space, preserve HTML
                  currentHtml = currentHtml.replace(/\s+$/, ''); // trim trailing whitespace
                  newHtml = currentHtml + ' ' + variable;
                  console.log('[Variable] Has content - appending variable with space');
                }

                console.log('[Variable] New HTML content:', newHtml);

                // Set the content directly on the component model
                // This avoids issues with MJML wrapper structures
                selected.components(newHtml);

                toast({
                  title: 'Variable inserted',
                  description: `${variable} has been added to your text`,
                });

                console.log('[Variable] Inserted into text successfully');
              } else {
                console.error('[Variable] Could not get component element');
                toast({
                  title: 'Insertion failed',
                  description: 'Could not access component content',
                  variant: 'destructive'
                });
              }
            }

            // Handle button components
            else if (componentType === 'mj-button') {
              // Validate that the variable is a URL type
              if (!isUrlVariable(variable)) {
                const varName = variable.replace(/{{|}}/g, '');
                toast({
                  title: 'Invalid variable for button',
                  description: `${varName} is not a URL variable. Only dynamic URLs, unsubscribe links, and view-in-browser links can be used in buttons.`,
                  variant: 'destructive'
                });
                console.warn('[Variable] Non-URL variable blocked from button:', variable);
                return;
              }

              // Get current href (if any)
              const currentHref = selected.getAttributes().href || '';

              console.log('[Variable] Current button href:', currentHref);

              // Set href attribute to variable
              selected.addAttributes({ href: variable });

              console.log('[Variable] New button href:', variable);

              toast({
                title: 'Button URL updated',
                description: `Button link set to ${variable}`,
              });

              console.log('[Variable] Inserted into button href successfully:', variable);
            }

            // Unsupported component type
            else {
              toast({
                title: 'Invalid component',
                description: 'Variables can be inserted into text blocks or buttons only',
                variant: 'destructive'
              });

              console.warn('[Variable] Unsupported component type:', componentType);
            }
          }
        });

        // Command: Open variable modal (personalization tab)
        editor.Commands.add('open-variable-modal', {
          run() {
            setVariableModalMode('personalization');
            setShowVariableModal(true);
          }
        });

        // Command: Open dynamic URL modal
        editor.Commands.add('open-dynamic-url-modal', {
          run() {
            setVariableModalMode('dynamic');
            setShowVariableModal(true);
          }
        });

        // Command: Open promo code modal
        editor.Commands.add('open-promo-modal', {
          run() {
            setVariableModalMode('promo');
            setShowVariableModal(true);
          }
        });

        // ====== CUSTOM TOOLBAR BUTTONS ======

        // Add buttons to the existing "options" panel (top toolbar)
        const optionsPanel = editor.Panels.getPanel('options');

        if (optionsPanel) {
          // Add variable insertion buttons
          optionsPanel.get('buttons').add([
            {
              id: 'insert-variable-btn',
              className: 'fa fa-user',
              command: 'open-variable-modal',
              attributes: {
                title: 'Insert Customer Variable'
              },
              active: false
            },
            {
              id: 'insert-dynamic-url-btn',
              className: 'fa fa-link',
              command: 'open-dynamic-url-modal',
              attributes: {
                title: 'Insert Dynamic URL'
              },
              active: false
            },
            {
              id: 'insert-promo-btn',
              className: 'fa fa-tag',
              command: 'open-promo-modal',
              attributes: {
                title: 'Insert Promo Code'
              },
              active: false
            }
          ]);

          console.log('[GrapesJS] Variable insertion buttons added to options panel');
        } else {
          console.warn('[GrapesJS] Options panel not found, creating new panel');

          // Fallback: create new panel
          editor.Panels.addPanel({
            id: 'personalization-tools',
            buttons: [
              {
                id: 'insert-variable-btn',
                className: 'fa fa-user',
                command: 'open-variable-modal',
                attributes: { title: 'Insert Customer Variable' }
              },
              {
                id: 'insert-dynamic-url-btn',
                className: 'fa fa-link',
                command: 'open-dynamic-url-modal',
                attributes: { title: 'Insert Dynamic URL' }
              },
              {
                id: 'insert-promo-btn',
                className: 'fa fa-tag',
                command: 'open-promo-modal',
                attributes: { title: 'Insert Promo Code' }
              }
            ]
          });
        }

        console.log('[GrapesJS] Variable insertion commands and toolbar added');

        // ====== RTE TOOLBAR BUTTON (Phase 3) ======
        // Add custom button to Rich Text Editor toolbar for cursor-aware insertion
        editor.RichTextEditor.add('insertVariable', {
          // Icon - @ symbol for variables
          icon: '<i class="fa fa-at"></i>',

          // HTML attributes
          attributes: {
            title: 'Insert Variable ({{firstName}}, {{promoCode}}, etc.)',
            class: 'gjs-rte-action-insert-var'
          },

          // Action executed when button clicked
          result: (rte) => {
            // Store RTE reference for later insertion
            rteRef.current = rte;

            // Set mode to cursor-aware insertion
            setInsertionMode('cursor');

            // Open variable modal (default to personalization tab)
            setVariableModalMode('personalization');
            setShowVariableModal(true);

            console.log('[RTE] Variable button clicked, cursor mode activated');
            console.log('[RTE] Current saved selection:', savedSelectionRef.current);
          }
        });

        console.log('[RTE] Custom toolbar button added');

        // Cursor tracking is now set up in the 'load' event handler (after canvas is ready)

        // Expose manual save function and editor instance to parent component
        if (onEditorReady) {
          const manualSave = async () => {
            console.log('[GrapesJS] Manual save triggered');
            await editor.store();
          };
          onEditorReady(manualSave, editor);
        }

        console.log('[GrapesJS] Editor initialized successfully');
        setIsLoading(false);

      } catch (error) {
        console.error('[GrapesJS] Initialization error:', error);
        toast({
          title: 'Editor Error',
          description: 'Failed to initialize email editor',
          variant: 'destructive'
        });
        setIsLoading(false);
      }
    };

    initEditor();

    // Cleanup
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [user?.organisationId, projectId, projectType, height]);

  /**
   * Load starter template with MJML structure
   */
  const loadStarterTemplate = (editor: Editor) => {
    const starterMJML = `<mjml>
  <mj-body background-color="#F5F5F5">
    <mj-section background-color="#FFFFFF" padding="40px 20px">
      <mj-column>
        <mj-text font-size="24px" font-weight="bold" color="#383838" padding-bottom="20px">
          Start Building Your Email
        </mj-text>
        <mj-text font-size="16px" color="#737373" line-height="1.6">
          Drag blocks from the right panel to add content to your email.
        </mj-text>
        <mj-button background-color="#3F92FF" color="#FFFFFF" href="#" padding-top="20px">
          Example Button
        </mj-button>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

    editor.setComponents(starterMJML);
    console.log('[Editor] Starter template loaded');
  };

  /**
   * Load initial assets from media library
   */
  const loadInitialAssets = async (editor: Editor) => {
    try {
      console.log('[Assets] Loading initial assets from media library');

      // Check if AssetManager is available
      if (!editor.AssetManager) {
        console.error('[Assets] AssetManager not available yet');
        return;
      }

      // IMPORTANT: Clear existing assets before adding new ones
      // This is crucial when reloading after loadProjectData() clears the AssetManager
      const currentAssets = editor.AssetManager.getAll();
      console.log('[Assets] Clearing', currentAssets.length, 'existing assets');
      editor.AssetManager.clear();

      const images = await MediaService.fetchImages(user!.organisationId, {
        status: 'completed',
        limit: 100
      });

      console.log(`[Assets] Fetched ${images.length} images`);

      // Explicitly sort by created_at descending (newest first) on frontend
      // This ensures consistent ordering even if backend sorting changes
      const sortedImages = [...images].sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA; // Descending: newest first
      });

      console.log('[Assets] First 3 images after sort:', sortedImages.slice(0, 3).map(img => ({
        filename: img.original_filename,
        created_at: img.created_at
      })));

      // IMPORTANT: AssetManager.add() PREPENDS items (adds to beginning of DOM)
      // We have: [newest, 2nd, oldest]
      // We need to reverse before adding so oldest is added first (prepended to top)
      // Then 2nd, then newest (prepended above oldest)
      // Final DOM order: newest at top ✓
      sortedImages.reverse().forEach(img => {
        editor.AssetManager.add({
          src: img.public_url,
          name: img.original_filename || 'Untitled',
          type: 'image',
          id: img.id
        });
      });

      console.log('[Assets] Added', sortedImages.length, 'assets to AssetManager');

      console.log('[Assets] Initial assets loaded successfully');

    } catch (error) {
      console.error('[Assets] Error loading initial assets:', error);
      toast({
        title: 'Asset Load Error',
        description: 'Failed to load media library images',
        variant: 'destructive'
      });
    }
  };

  /**
   * Setup editor event listeners
   */
  const setupEditorEvents = (editor: Editor) => {
    editor.on('update', () => {
      console.log('[Editor] Content updated');
    });

    editor.on('storage:start', () => {
      console.log('[Storage] Save started');
    });

    editor.on('storage:end', () => {
      console.log('[Storage] Save completed');
    });

    editor.on('storage:error', (error) => {
      console.error('[Storage] Save error:', error);
    });

    editor.on('asset:add', (asset) => {
      console.log('[Asset] Added:', asset.get('src'));
    });

    editor.on('load', () => {
      console.log('[Editor] Loaded successfully');
    });

    // Upload event listeners
    editor.on('asset:upload:start', () => {
      console.log('[Assets] Upload started');
      setSaveStatus('saving'); // Reuse save status for upload indicator
    });

    editor.on('asset:upload:end', () => {
      console.log('[Assets] Upload ended');
      setSaveStatus('idle');
    });

    editor.on('asset:upload:error', (error) => {
      console.error('[Assets] Upload error:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    });

    editor.on('asset:upload:response', (response) => {
      console.log('[Assets] Upload response received:', response);
    });
  };

  /**
   * Handle variable selection from modal
   * Supports two modes:
   * 1. Cursor mode (from RTE button) - insert at cursor position
   * 2. Append mode (from top toolbar) - append to end of component
   */
  const handleVariableSelect = (variable: string) => {
    console.log('[Variable] Selected:', variable, 'Mode:', insertionMode);

    // MODE 1: Cursor-aware insertion (from RTE toolbar button)
    if (insertionMode === 'cursor') {
      try {
        const editor = editorRef.current;
        const selected = editor?.getSelected();

        if (!selected || selected.get('type') !== 'mj-text') {
          throw new Error('No text component selected');
        }

        // ========== FIX: Restore cursor from character offset ==========
        // ISSUE: Range objects become invalid on blur - need offset-based restoration
        // SOLUTION: Recreate Range from saved character offset
        const { offset, contentDiv } = savedSelectionRef.current;

        if (contentDiv === null) {
          throw new Error('No saved cursor position');
        }

        console.log('[Variable] Restoring cursor at offset:', offset);

        // Recreate Range from offset by walking through text nodes
        const range = document.createRange();
        let charCount = 0;
        let found = false;

        const walkTextNodes = (node: Node): boolean => {
          if (node.nodeType === Node.TEXT_NODE) {
            const textLength = node.textContent?.length || 0;
            if (charCount + textLength >= offset) {
              // Found the target position
              range.setStart(node, offset - charCount);
              range.setEnd(node, offset - charCount);
              return true;
            }
            charCount += textLength;
          } else {
            for (let i = 0; i < node.childNodes.length; i++) {
              if (walkTextNodes(node.childNodes[i])) {
                return true;
              }
            }
          }
          return false;
        };

        found = walkTextNodes(contentDiv);

        if (!found) {
          // Cursor was at end - place at end of last text node
          range.selectNodeContents(contentDiv);
          range.collapse(false);
        }

        // Insert variable at reconstructed cursor position
        const textNode = document.createTextNode(variable);
        range.insertNode(textNode);

        // Move cursor after inserted variable
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);

        // Update saved offset for next insertion
        const newOffset = offset + variable.length;
        savedSelectionRef.current = { offset: newOffset, contentDiv };
        console.log('[Variable] Inserted at offset', offset, 'new offset:', newOffset);
        // ========== END FIX ==========

        // ========== FIX: Sync RTE DOM to Component Model ==========
        // ISSUE: Direct DOM manipulation doesn't update the GrapesJS component model
        // SOLUTION: Manually sync the content back to component model
        const el = selected.getEl();
        if (el) {
          const contentDiv = el.querySelector('div[contenteditable]')
                          || el.querySelector('div:last-of-type')
                          || el;

          // Sync the updated innerHTML to the component model
          selected.components(contentDiv.innerHTML);

          console.log('[Variable] RTE content synced to component model');
        }
        // ========== END FIX ==========

        toast({
          title: 'Variable inserted',
          description: `${variable} inserted at cursor position`,
        });

        console.log('[Variable] Inserted via cursor position restoration');
      } catch (error) {
        console.error('[Variable] RTE insertion failed, falling back to append:', error);

        // Fallback to append mode if cursor insertion fails
        if (editorRef.current) {
          editorRef.current.runCommand('insert-variable', { variable });
        }
      }
    }
    // MODE 2: Append mode (from top toolbar buttons)
    else {
      if (editorRef.current) {
        editorRef.current.runCommand('insert-variable', { variable });
      }
    }

    // Cleanup: Close modal and reset state
    setShowVariableModal(false);
    setInsertionMode('append');
    rteRef.current = null;
  };

  /**
   * Handle promo code selection from modal
   * Supports two modes (same as handleVariableSelect):
   * 1. Cursor mode (from RTE button) - insert at cursor position
   * 2. Append mode (from top toolbar) - append to end of component
   */
  const handlePromoCodeSelect = (code: string, codeId: number, promoDetails?: PromoCode) => {
    if (editorRef.current) {
      const selected = editorRef.current.getSelected();
      const componentType = selected?.get('type');

      console.log('[Promo Code] Selected:', code, 'ID:', codeId, 'Mode:', insertionMode);

      // Validation: Promo codes cannot be inserted into buttons
      if (componentType === 'mj-button') {
        toast({
          title: 'Cannot insert promo code in button',
          description: 'Promo codes can only be inserted into text blocks. Please select a text component.',
          variant: 'destructive'
        });
        setShowVariableModal(false);
        setInsertionMode('append');
        rteRef.current = null;
        return;
      }

      // MODE 1: Cursor-aware insertion (from RTE toolbar button)
      if (insertionMode === 'cursor') {
        try {
          const editor = editorRef.current;
          const selected = editor?.getSelected();

          if (!selected || selected.get('type') !== 'mj-text') {
            throw new Error('No text component selected');
          }

          // ========== FIX: Same offset-based cursor restoration as variables ==========
          const { offset, contentDiv } = savedSelectionRef.current;

          if (contentDiv === null) {
            throw new Error('No saved cursor position');
          }

          console.log('[Promo Code] Restoring cursor at offset:', offset);

          // Recreate Range from offset by walking through text nodes
          const range = document.createRange();
          let charCount = 0;
          let found = false;

          const walkTextNodes = (node: Node): boolean => {
            if (node.nodeType === Node.TEXT_NODE) {
              const textLength = node.textContent?.length || 0;
              if (charCount + textLength >= offset) {
                range.setStart(node, offset - charCount);
                range.setEnd(node, offset - charCount);
                return true;
              }
              charCount += textLength;
            } else {
              for (let i = 0; i < node.childNodes.length; i++) {
                if (walkTextNodes(node.childNodes[i])) {
                  return true;
                }
              }
            }
            return false;
          };

          found = walkTextNodes(contentDiv);

          if (!found) {
            range.selectNodeContents(contentDiv);
            range.collapse(false);
          }

          // Insert promo code at reconstructed cursor position
          const textNode = document.createTextNode(code);
          range.insertNode(textNode);

          // Move cursor after inserted code
          range.setStartAfter(textNode);
          range.setEndAfter(textNode);

          // Update saved offset for next insertion
          const newOffset = offset + code.length;
          savedSelectionRef.current = { offset: newOffset, contentDiv };
          console.log('[Promo Code] Inserted at offset', offset, 'new offset:', newOffset);
          // ========== END FIX ==========

          // ========== FIX: Sync RTE DOM to Component Model ==========
          const el = selected.getEl();
          if (el) {
            const contentDiv = el.querySelector('div[contenteditable]')
                            || el.querySelector('div:last-of-type')
                            || el;

            selected.components(contentDiv.innerHTML);
            console.log('[Promo Code] RTE content synced to component model');
          }
          // ========== END FIX ==========

          toast({
            title: 'Promo code inserted',
            description: `Code "${code}" inserted at cursor position`,
          });

          console.log('[Promo Code] Inserted via offset-based cursor restoration');
        } catch (error) {
          console.error('[Promo Code] RTE insertion failed, falling back to append:', error);

          // Fallback to append mode
          editorRef.current.runCommand('insert-variable', { variable: code });
        }
      }
      // MODE 2: Append mode (from top toolbar buttons)
      else {
        // Insert the actual promo code value (not a variable)
        editorRef.current.runCommand('insert-variable', { variable: code });

        toast({
          title: 'Promo code inserted',
          description: `Code "${code}" has been added to your email`,
        });

        console.log('[Promo Code] Inserted via append mode');
      }

      // Cleanup: Close modal and reset state
      setShowVariableModal(false);
      setInsertionMode('append');
      rteRef.current = null;

      // TODO: Update campaign with promo_code_id for attribution tracking
      console.log('[Promo Code] Tracking - Code ID:', codeId);
    }
  };

  /**
   * Get current email content for variable highlighting
   */
  const getCurrentEmailContent = (): string => {
    if (!editorRef.current) return '';
    try {
      return editorRef.current.getHtml();
    } catch (error) {
      return '';
    }
  };

  /**
   * Handle MediaPicker selection
   * Uses GrapesJS Trait API which properly integrates with MJML
   */
  const handleMediaSelect = (media: RestaurantMedia) => {
    if (!editorRef.current) {
      console.error('[MediaPicker] Editor not initialized');
      return;
    }

    console.log('[MediaPicker] Selected media:', media.id, media.public_url);

    const editor = editorRef.current;
    const target = targetImageComponent || editor.getSelected();

    // Add asset to AssetManager
    let asset = editor.AssetManager.get(media.public_url);
    if (!asset) {
      asset = editor.AssetManager.add({
        src: media.public_url,
        name: media.original_filename || 'Image',
        type: 'image',
        id: media.id
      });
    }

    if (target && target.get('type') === 'mj-image') {
      console.log('[MediaPicker] Triggering asset:custom event for mj-image');

      // Trigger the asset:custom event that our listener will handle
      // This approach lets GrapesJS handle the update in its normal flow
      editor.trigger('asset:custom', asset);

      toast({
        title: 'Image Applied',
        description: `${media.original_filename || 'Image'} added to email`,
      });
    } else {
      console.log('[MediaPicker] No image component selected');
      toast({
        title: 'Image Added to Library',
        description: `${media.original_filename || 'Image'} is available. Click an image block to use it.`,
      });
    }

    // Close MediaPicker and clear target
    setShowMediaPicker(false);
    setTargetImageComponent(null);
  };

  return (
    <div className="relative w-full" style={{ height }}>
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading editor...</p>
          </div>
        </div>
      )}

      {/* Save status indicator */}
      {saveStatus !== 'idle' && (
        <div className="absolute top-4 right-4 z-50">
          <div className={`px-3 py-1 rounded-md text-sm font-medium ${
            saveStatus === 'saving' ? 'bg-blue-100 text-blue-800' :
            saveStatus === 'saved' ? 'bg-green-100 text-green-800' :
            'bg-red-100 text-red-800'
          }`}>
            {saveStatus === 'saving' ? 'Saving...' :
             saveStatus === 'saved' ? 'Saved ✓' :
             'Save failed'}
          </div>
        </div>
      )}

      {/* Editor container */}
      <div
        ref={containerRef}
        id="gjs"
        className="w-full h-full"
      />

      {/* REMOVED: MediaPicker Dialog - now using native GrapesJS AssetManager */}

      {/* Variable Insertion Modal */}
      <VariableInsertionModal
        open={showVariableModal}
        onClose={() => setShowVariableModal(false)}
        onVariableSelect={handleVariableSelect}
        onPromoCodeSelect={handlePromoCodeSelect}
        restaurants={restaurants}
        selectedRestaurantId={selectedRestaurantId}
        onRestaurantChange={setSelectedRestaurantId}
        currentEmailContent={getCurrentEmailContent()}
        initialMode={variableModalMode}
        allowDynamicVariables={true}
      />
    </div>
  );
};

export default GrapesJSEditor;
